import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useHealthNudges } from '@/hooks/use-health-nudges';
import { GlassCard } from '@/components/ui/glass-card';
import HealthNudgeCard from './HealthNudgeCard';
import { CheckCircle } from 'lucide-react';

export default function NudgeFeed() {
  const { nudges, unreadCount, dismissNudge, snoozeNudge, isLoading } = useHealthNudges();
  const navigate = useNavigate();
  const [showAll, setShowAll] = useState(false);

  // Sort by priority (high -> medium -> low), then unread first
  const sortedNudges = [...nudges].sort((a, b) => {
    const priorityOrder = { high: 0, medium: 1, low: 2 };
    const priorityDiff =
      priorityOrder[a.priority as keyof typeof priorityOrder] -
      priorityOrder[b.priority as keyof typeof priorityOrder];
    if (priorityDiff !== 0) return priorityDiff;
    return a.isRead === b.isRead ? 0 : a.isRead ? 1 : -1;
  });

  const displayedNudges = showAll ? sortedNudges : sortedNudges.slice(0, 5);

  const handleAction = (path: string) => {
    navigate(path);
  };

  if (isLoading) {
    return (
      <GlassCard className="w-full">
        <div className="space-y-4 p-6">
          <div className="flex items-center justify-between">
            <div className="h-6 w-40 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
            <div className="h-6 w-6 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse" />
          </div>
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
          ))}
        </div>
      </GlassCard>
    );
  }

  return (
    <GlassCard className="w-full">
      <div className="space-y-4 p-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">
            Health Nudges
          </h2>
          {unreadCount > 0 && (
            <div className="flex items-center justify-center h-6 min-w-[24px] px-2 rounded-full bg-blue-600 text-white text-xs font-bold">
              {unreadCount}
            </div>
          )}
        </div>

        {/* Nudge List */}
        {nudges.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="h-16 w-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-4">
              <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
            </div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">
              All caught up!
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              No new nudges - you're doing great!
            </p>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              {displayedNudges.map((nudge) => (
                <HealthNudgeCard
                  key={nudge.id}
                  nudge={nudge}
                  onDismiss={dismissNudge}
                  onSnooze={(id: string) => snoozeNudge({ nudgeId: id, hours: 24 })}
                  onAction={handleAction}
                />
              ))}
            </div>

            {/* Show More Button */}
            {nudges.length > 5 && (
              <button
                onClick={() => setShowAll(!showAll)}
                className="w-full py-2 px-4 rounded-lg border border-gray-300 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                {showAll ? 'Show Less' : `Show ${nudges.length - 5} More`}
              </button>
            )}
          </>
        )}
      </div>
    </GlassCard>
  );
}
